import {
  DecisionStatusEnum,
  HStack,
  OrderStatus,
  Text,
  abbreviateId,
  isOrder,
  useSecurity,
  type ExecutionReport,
  type MarketOrder,
  type Order,
} from '@talos/kyoko';
import { useCallback, useEffect } from 'react';
import { useMaxOrderSizeDialogs } from '../../../../hooks/useMaxOrderSizeDialogs';
import { Row } from './styles';
import type { OrderValidationResult } from './useBulkResumeValidationState';

interface SelectedOrdersListProps {
  selectedOrders: (Order | ExecutionReport | MarketOrder)[];
  isDialogOpen?: boolean;
  onValidationResult?: (orderId: string, result: OrderValidationResult) => void;
  orderStatuses?: Map<string, 'warn' | 'reject'>;
}

interface SelectedOrdersListItemProps {
  index: number;
  isDialogOpen?: boolean;
  onValidationResult?: (orderId: string, result: OrderValidationResult) => void;
  orderStatuses?: Map<string, 'warn' | 'reject'>;
  selectedOrder: Order | ExecutionReport | MarketOrder;
  totalCount: number;
}

function isValidatableOrder(selectedOrder: Order | ExecutionReport | MarketOrder): selectedOrder is Order {
  return Boolean(selectedOrder.OrderID && selectedOrder.OrderQty && selectedOrder.Symbol);
}

function SelectedOrderValidationReporter({
  isDialogOpen,
  onValidationResult,
  selectedOrder,
}: {
  isDialogOpen?: boolean;
  onValidationResult?: (orderId: string, result: OrderValidationResult) => void;
  selectedOrder: Order | ExecutionReport | MarketOrder;
}) {
  const validatableOrder = isValidatableOrder(selectedOrder) ? selectedOrder : undefined;
  const shouldValidate = isDialogOpen === true && onValidationResult != null && validatableOrder != null;
  const orderId = validatableOrder?.OrderID;
  const orderQty = validatableOrder?.OrderQty;
  const security = useSecurity(shouldValidate ? validatableOrder.Symbol : undefined);
  const handleValidationStateChange = useCallback(
    (state: OrderValidationResult['status']) => {
      if (shouldValidate && orderId != null && state !== 'idle') {
        onValidationResult(orderId, { status: state });
      }
    },
    [onValidationResult, orderId, shouldValidate]
  );
  const { validate } = useMaxOrderSizeDialogs({
    currency: shouldValidate ? (validatableOrder.Currency ?? '') : undefined,
    onValidationStateChange: handleValidationStateChange,
    symbol: shouldValidate ? validatableOrder.Symbol : undefined,
    security,
    onConfirmWarning: () => {},
  });

  useEffect(() => {
    if (shouldValidate && orderQty != null) {
      validate({ size: orderQty });
    }
  }, [shouldValidate, validate, orderQty]);

  return null;
}

function SelectedOrdersListItem({
  index,
  isDialogOpen,
  onValidationResult,
  orderStatuses,
  selectedOrder,
  totalCount,
}: SelectedOrdersListItemProps) {
  const id = (selectedOrder as MarketOrder).OID ?? `#${abbreviateId(selectedOrder.OrderID ?? '')}`;
  const limitStatus = orderStatuses?.get(selectedOrder.OrderID ?? '');

  return (
    <>
      {onValidationResult != null && (
        <SelectedOrderValidationReporter
          isDialogOpen={isDialogOpen}
          onValidationResult={onValidationResult}
          selectedOrder={selectedOrder}
        />
      )}
      {index < 5 && (
        <Row>
          <Text>{id}</Text>
          <HStack gap="spacing8">
            <OrderStatus
              ordStatus={selectedOrder.OrdStatus}
              decisionStatus={isOrder(selectedOrder) ? selectedOrder?.DecisionStatus : DecisionStatusEnum.Active}
              cumQty={selectedOrder.CumQty}
              orderQty={selectedOrder.OrderQty}
              pricingMode={isOrder(selectedOrder) ? selectedOrder.PricingMode : undefined}
              title={selectedOrder.Text}
              isCustomerOrder={false}
            />
            {limitStatus === 'reject' && <Text color="colorTextNegative">Reject limit</Text>}
            {limitStatus === 'warn' && <Text color="colorTextWarning">Warn limit</Text>}
          </HStack>
        </Row>
      )}
      {index >= 5 && index === totalCount - 1 && <Row>+ {totalCount - 5} Orders</Row>}
    </>
  );
}

export function SelectedOrdersList({
  selectedOrders,
  isDialogOpen,
  onValidationResult,
  orderStatuses,
}: SelectedOrdersListProps) {
  if (!selectedOrders?.length) {
    return null;
  }

  return (
    <>
      {selectedOrders.map((selectedOrder, index) => (
        <SelectedOrdersListItem
          key={(selectedOrder as MarketOrder).OID ?? selectedOrder.OrderID}
          index={index}
          isDialogOpen={isDialogOpen}
          onValidationResult={onValidationResult}
          orderStatuses={orderStatuses}
          selectedOrder={selectedOrder}
          totalCount={selectedOrders.length}
        />
      ))}
    </>
  );
}
